import {merge, Subject, BehaviorSubject, ReplaySubject} from 'rxjs';
import {switchMap, filter, map, share, startWith} from 'rxjs/operators';
import Big from 'big.js';
import {mc} from '@/core';
import {envUtil} from '@/helper/env';
import {numUtil} from '@/helper/num';
import {moTradeCompetitionApi} from '@/api/mo-trade-competition';
import {browser$} from '@/streams/browser';
import {userStatus$} from '@/streams/auth';
import {contracts$} from '@/streams/config/contract';
import {PRIZE_COIN_NAME} from './const';

const GAME_ID = '14';

export const joinNow$ = new Subject();
export const refresh$ = new ReplaySubject();
export const gameInfo$ = new BehaviorSubject({});
export const gameStatus$ = new BehaviorSubject({
  isBeforeSignUpStart: false,
  isSignUpStart: false,
  isCompetitionStart: false,
  isBeforeCompetition: false,
  isCompetitionEnd: false,
  initialed: false,
});
export const myEntryInfo$ = new BehaviorSubject({});
export const rankings$ = new BehaviorSubject({
  trading: [],
  PNL: [],
});
export const prizePoolLevels$ = new BehaviorSubject([
  {
    count: 1,
  },
  {
    count: 250,
    prize: 7600,
  },
  {
    count: 500,
    prize: 13100,
  },
  {
    prize: 20000,
  },
]);

const join$ = joinNow$.pipe(
  switchMap(() =>
    moTradeCompetitionApi.joinCompetition({
      competitionId: GAME_ID,
      teamFlag: false,
      channel: envUtil.isMobile ? 'phone' : 'pc',
      scene: 'COMPETITION_MEMBER_JOIN',
    })
  ),
  share()
);

export const joinSuccess$ = join$.pipe(filter(({data}) => !!data));
export const joinFail$ = join$.pipe(
  filter(({error}) => !!error),
  map(({error}) => error)
);
export const myEntryInfoLoaded$ = myEntryInfo$.pipe(filter((info) => info.isJoinedCompetition !== undefined));

const refreshWithUserLogin$ = refresh$.pipe(
  startWith(0),
  switchMap(() => userStatus$),
  filter((userStatus) => userStatus === 'Login')
);

merge(refreshWithUserLogin$, joinSuccess$).subscribe(() => loadUserInfo());
merge(browser$, joinSuccess$, refresh$).subscribe(() => loadGameInfo());

const tradeContract$ = new BehaviorSubject({});
contracts$
  .pipe(
    filter(Boolean),
    map((contracts) => {
      return contracts.find((c) => c.symbol === 'uBTCUSD');
    })
  )
  .subscribe((tc) => {
    tradeContract$.next(tc);
  });
// 比赛开始后就加载排名信息
gameStatus$.pipe(filter((data) => data.initialed && !data.isBeforeCompetition)).subscribe(() => loadRankList());

function loadGameInfo() {
  moTradeCompetitionApi.getCompetitionInfo(GAME_ID).then(({data, error}) => {
    if (data) {
      const competitionStartTime = new Date(data.competitionStartTime);
      const competitionEndTime = new Date(data.competitionEndTime);
      const signUpStartTime = new Date(data.playerRegisterStartTime);
      const signUpEndTime = new Date(data.competitionStartTime);
      const awardsStartTime = new Date(data.competitionEndTime);
      const awardsEndTime = new Date(data.awardsTime);
      gameInfo$.next({
        currentPool: calcPrizePool(data.totalMemberCnt),
        participantCount: data.totalMemberCnt,
        competitionStartTime,
        competitionEndTime,
        signUpStartTime,
        signUpEndTime,
        awardsStartTime,
        awardsEndTime,
      });
      const now = Date.now();
      gameStatus$.next({
        isBeforeSignUpStart: now < signUpStartTime,
        isSignUpStart: now > signUpStartTime && now < competitionStartTime,
        isBeforeCompetition: now < competitionStartTime,
        isCompetitionStart: now > competitionStartTime && now < competitionEndTime,
        isCompetitionEnd: now > competitionEndTime,
        initialed: true,
      });
      return;
    }
    if (error) {
      mc.send('toast', 'error', error);
    }
  });
}

function calcPrizePool(participantCount) {
  const levels = prizePoolLevels$.getValue();
  for (let i = 0; i < levels.length - 1; i++) {
    const level = levels[i];
    if (participantCount < level.count) {
      return level.prize || 0;
    }
  }
  return levels[levels.length - 1].prize;
}

function calcTradeVolume(quantity) {
  const contract = tradeContract$.getValue();
  const {contractUnderlyingAssets, contractSize} = contract;
  return Big(contractSize).times(quantity).toFixed(3) + ' ' + contractUnderlyingAssets;
}

function loadUserInfo() {
  moTradeCompetitionApi.getMyCompetitionTradeInfo(GAME_ID).then(({data, error}) => {
    if (data) {
      const {competitionMember, rankMap} = data;
      if (competitionMember) {
        const newData = {
          isJoinedCompetition: true,
          tradingVolume: calcTradeVolume(competitionMember.usdCumValueEv),
          // rankMap, 1 为tradeVolume排名，2，为ROI 排名
          tradingRanking: rankMap ? `No.${rankMap.G_TRADE}` : '--',
          PNLRanking: rankMap ? `No.${rankMap.G_ROI}` : '--',
        };
        myEntryInfo$.next(newData);
        return;
      }
      myEntryInfo$.next({
        isJoinedCompetition: false,
      });
    }
    if (error) {
      myEntryInfo$.next({});
      mc.send('toast', 'error', error);
    }
  });
}

function loadRankList() {
  moTradeCompetitionApi.getTradeVolumeRankList(GAME_ID).then(({data, error}) => {
    const originData = rankings$.getValue();
    if (data) {
      const tradingList = data.dataList.map((item) => {
        return {
          name: item.userNickname,
          score: calcTradeVolume(item.usdCumValueEv),
          prize: numUtil.floor(item.prizeEv || 0, 2, 8) + ' ' + PRIZE_COIN_NAME,
        };
      });
      rankings$.next({...originData, trading: tradingList});
      return;
    }

    if (error) {
      rankings$.next({...originData, trading: []});
      mc.send('toast', 'error', error);
    }
  });
}
